import { Box, Divider, FormControlLabel, Grid, Switch, Theme } from '@mui/material';
import { Field, FieldProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolSelect, PoolTextField, PoolTypography, SelectOption } from '@synergy/components';
import { PoolTooltip } from '@synergy/components-next';
import { fields } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useRef } from 'react';

import { ProductPriceFormValues } from '../models/form-values/product-price-form-values';
import { TaxClass } from '../models/tax-class';

const useStyles = makeStyles((theme: Theme) => ({
    basicInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    basicInfoContent: {
        marginBottom: theme.spacing(6),
    },
    switch: {
        height: 20,
        marginRight: 0,
    },
    divider: {
        marginTop: 8,
        marginBottom: 16,
        backgroundColor: theme.palette.text.primary,
    },
}));

export const ProductDetailPricesBasicInfos = () => {
    const classes = useStyles();
    const fieldNames = useRef(fields<ProductPriceFormValues>()).current;

    const taxClassOptions: SelectOption[] = [
        {
            value: TaxClass.Standard,
            displayValue: <FormattedMessage id="app.product.detail.prices.taxClassStandard" />,
        },
        {
            value: TaxClass.Reduced1,
            displayValue: <FormattedMessage id="app.product.detail.prices.taxClassReduced1" />,
        },
        {
            value: TaxClass.Reduced2,
            displayValue: <FormattedMessage id="app.product.detail.prices.taxClassReduced2" />,
        },
        {
            value: TaxClass.Reduced3,
            displayValue: <FormattedMessage id="app.product.detail.prices.taxClassReduced3" />,
        },
    ];

    return (
        <>
            <Box className={classes.basicInfo}>
                <PoolTypography typography="h6" id="app.common.basicInfos" />

                <Field name={fieldNames.isBestPrice}>
                    {({ field }: FieldProps<boolean>) => (
                        <PoolTooltip
                            title={<FormattedMessage id="app.product.detail.prices.bestpriceTooltip" />}
                            placement="top"
                        >
                            <FormControlLabel
                                control={
                                    <Switch color="primary" checked={field.value} name={field.name} disabled={true} />
                                }
                                label={<FormattedMessage id="app.product.detail.prices.bestpriceSwitch" />}
                                className={classes.switch}
                            />
                        </PoolTooltip>
                    )}
                </Field>
            </Box>
            <Divider className={classes.divider} />

            <Grid container={true} spacing={2} className={classes.basicInfoContent}>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.orderUnit}>
                        {({ field }: FieldProps<string>) => (
                            <PoolTextField
                                fullWidth={true}
                                value={field.value}
                                label={<FormattedMessage id="app.label.orderUnit" />}
                                readOnly={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.contentUnit}>
                        {({ field }: FieldProps<string>) => (
                            <PoolTextField
                                fullWidth={true}
                                value={field.value}
                                label={<FormattedMessage id="app.label.contentUnit" />}
                                readOnly={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.contentQuantity}>
                        {({ field }: FieldProps<number>) => (
                            <PoolTextField
                                fullWidth={true}
                                value={field.value}
                                label={<FormattedMessage id="app.label.contentQuantity" />}
                                isNumeric={true}
                                readOnly={true}
                            />
                        )}
                    </Field>
                </Grid>
                <Grid item={true} xs={3}>
                    <Field name={fieldNames.minimumOrderQuantity}>
                        {({ field }: FieldProps<number>) => (
                            <PoolTextField
                                fullWidth={true}
                                value={field.value}
                                label={<FormattedMessage id="app.label.minimumOrderQuantity" />}
                                isNumeric={true}
                                readOnly={true}
                            />
                        )}
                    </Field>
                </Grid>

                <Grid item={true} xs={3}>
                    <Field name={fieldNames.priceQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.label.priceQuantity" />}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                required={true}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>

                <Grid item={true} xs={3}>
                    <Field name={fieldNames.priceReferenceQuantity}>
                        {({ field, meta }: FieldProps<number>) => (
                            <PoolTextField
                                {...field}
                                fullWidth={true}
                                label={<FormattedMessage id="app.label.priceReferenceQuantity" />}
                                isNumeric={true}
                                noNegativeNumbers={true}
                                required={true}
                                error={meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                </Grid>

                <Grid item={true} xs={3}>
                    <Field name={fieldNames.taxClass}>
                        {({ field, form }: FieldProps<string>) => (
                            <PoolSelect
                                displayEmpty={true}
                                fullWidth={true}
                                label={<FormattedMessage id="app.product.detail.prices.taxes" />}
                                options={taxClassOptions}
                                noSorting={true}
                                required={true}
                                disallowDefaultValue={true}
                                onValueChange={value => form.setFieldValue(field.name, value)}
                                value={field.value}
                                name={field.name}
                            />
                        )}
                    </Field>
                </Grid>
            </Grid>
        </>
    );
};
